import { CircularProgress, Button, Box } from '@mui/material';
import { useState } from 'react';
import { getDocument } from '../../services/getDocument'
import { getWording } from '../../services/getWording'
import { MixpanelWrapper } from '../MixPanelWrapper';
import { useGetPolicyWordingsByPolicyIdListQuery, useGetWordingsByQuoteIdListQuery } from '../../services/features/documentsApi/documentsApi';
import { useQuoteContext } from '../../shared/contexts/QuoteContext';
import { NonApplicablePolicyDocuments } from '../../helper/nonApplicableDocuments';
import { DocumentType } from "../../helper/enum/DocumentType";

/**
 * Component for downloading all documents associated with a quote.
 *
 * This component provides a button that, when clicked, initiates the download of various types of documents
 * associated with one or more quotes.
 *
 * @param {Object} props - The props passed to the component.
 * @param {string} props.types - The type of documents to download ("all" or "quote").
 * @returns {JSX.Element} The rendered component for downloading documents.
 */
const DownloadAllDocuments = (props: any) => {
	const [loading, setLoading] = useState(false);

	const { currentQuotes, policyIds } = useQuoteContext();

	const { data: policyUrls } = useGetPolicyWordingsByPolicyIdListQuery(policyIds, {
		skip: policyIds.length === 0 || props.types !== 'all',
	});

	const { data: quoteUrls } = useGetWordingsByQuoteIdListQuery(currentQuotes.map(quote => quote.id), {
		skip: currentQuotes.length === 0,
	});
	/**
	 * Downloads documents associated with the specified quote IDs.
	 */
	const downloadDocuments = async () => {
		setLoading(true);
		MixpanelWrapper.track('Download All Quote Document');
		try {
			switch (props.types) {
				case 'all': {
					currentQuotes.forEach((quote) => {
						const wordings = policyUrls?.[quote.policyId];
						wordings?.forEach((wording) => {
							getWording(quote.id, wording.policyWordingUrl, wording.policyWordingName, true);
						});
					});
					await Promise.all(
						currentQuotes.flatMap((quote) => [
							getDocument(quote.id, DocumentType.Quote, true),
							...((quote.nonApplicablePolicyDocuments ?? []).includes(
								NonApplicablePolicyDocuments.CertificateOfCurrency
							)
								? []
								: [getDocument(quote.id, DocumentType.Certificate, true)]),
							getDocument(quote.id, DocumentType.Policy, true),
							getDocument(quote.id, DocumentType.DecisionTree, true)
						])
					);
					break;
				}
				default: {
					currentQuotes.forEach((quote) => {
						const wordings = quoteUrls?.[quote.id];
						wordings?.forEach((wording) => {
							getWording(quote.id, wording.policyWordingUrl, wording.policyWordingName, true);
						});
					});
					await Promise.all(
						currentQuotes.flatMap((quote) => [
							getDocument(quote.id, DocumentType.Quote, true),
							getDocument(quote.id, DocumentType.DecisionTree, true)
						])
					);
					break;
				}
			}
		} catch (error) {
			console.log(error);
		}
		setLoading(false);
	};

	return (
		<>
			<Button
				variant='contained'
				disabled={loading || props.disabled}
				color='secondary'
				onClick={async () => (loading ? null : await downloadDocuments())}
			>
				{loading ? (
					<CircularProgress
						size={'2em'}
						sx={{
							color: 'white',
						}}
					/>
				) : (
					'Download all'
				)}
			</Button>
		</>
	);
};

export default DownloadAllDocuments;
